import { cellAddress, columnLetter } from "./a1";

// Turning a column of formulas into one fact instead of thousands.
//
// This is the load-bearing idea behind fitting a spreadsheet in a prompt. A column of
// =B2*C2 filled down 50,000 rows is ONE piece of logic, not 50,000; and the logic is
// the most valuable thing in a workbook. Compressing it by column takes the formulas
// from O(rows) to O(columns) losslessly -- a 200-column model over 10,000 rows goes
// from two million facts to about two hundred.
//
// It is not just about size. The exceptions this surfaces -- a cell whose formula
// deviates from the rest of its column -- are exactly where spreadsheet bugs live: a
// hardcoded total, a mis-dragged reference, a one-off override. Listing them
// explicitly is more useful than any amount of sampled data.

/** A run of rows in one column sharing a single row-relative formula shape. */
export interface FormulaPattern {
  /** The range the run covers, e.g. "D2:D50234", or a single cell. */
  range: string;
  /**
   * The shared formula with row numbers made relative: "=B{r}*C{r}" means "the cell
   * to my left in this row, times the one to its left". "{r-1}" is the row above.
   */
  pattern: string;
  /** How many cells the run covers, so the reader knows its weight. */
  cellCount: number;
}

/** A cell whose formula does not match its column's pattern. Always reported. */
export interface FormulaException {
  address: string;
  formula: string;
}

export interface CompressedColumn {
  patterns: FormulaPattern[];
  exceptions: FormulaException[];
}

// (col)(optional $)(row digits). The lookarounds are the false-positive guards.
const CELL_REFERENCE = /(?<![A-Za-z0-9_$])(\$?)([A-Za-z]{1,3})(\$?)(\d+)(?![0-9(!])/g;

/**
 * Splits a formula into alternating [outside, literal, outside, literal, ...].
 *
 * Excel string literals are double-quoted and escape an inner quote by doubling it:
 * ="he said ""hi""". The capturing group means the literals land on odd indices.
 */
const splitOnStringLiterals = (formula: string) => formula.split(/("(?:[^"]|"")*")/);

/**
 * Rewrites a formula's row numbers as offsets from the row it sits on, so that the
 * same logic filled down a column produces one identical string.
 *
 * Absolute rows ($5) are left alone -- they genuinely mean row 5 everywhere, which is
 * what makes =SUM(B$1:B5) compress correctly.
 *
 * Three false positives are guarded against. All three would CORRUPT the formula
 * rather than merely fail to compress it, which is the worse outcome by far: a wrong
 * pattern makes the model reason about logic the workbook does not contain.
 *  - function names ending in digits: LOG10( must not become LOG{r-...}(, so a
 *    following "(" disqualifies a match;
 *  - sheet-qualified references: DATA1!B5 must keep its sheet name, so a following
 *    "!" disqualifies one;
 *  - text inside string literals: ="B5 is wrong" is prose, not a reference, so
 *    literals are excluded from rewriting entirely.
 */
export function normaliseFormula(formula: string, row: number): string {
  return splitOnStringLiterals(formula)
    .map((part, index) => {
      // Odd indices are the literals themselves. Never touch them.
      if (index % 2 === 1) return part;

      return part.replace(CELL_REFERENCE, (match, colAbs, letters, rowAbs, digits) => {
        // An absolute row means the same row everywhere; leave it verbatim.
        if (rowAbs === "$") return match;

        const offset = Number(digits) - 1 - row;
        const token = offset === 0 ? "{r}" : `{r${offset > 0 ? "+" : ""}${offset}}`;
        return `${colAbs}${letters}${token}`;
      });
    })
    .join("");
}

/**
 * The inverse of `normaliseFormula`: turns a {r}-offset pattern back into a real
 * formula for a given row.
 *
 * Needed because Excel's `formulas` setter writes each string LITERALLY. Assigning
 * "=B2*C2" to every cell of D2:D500 puts "=B2*C2" in all 499 of them -- it does not
 * fill down the way dragging does. To get fill-down behaviour the formula has to be
 * adjusted per row before it is written, which is exactly what this does.
 *
 * Only rows are offset, because that is the axis a formula gets filled along in
 * practice. Callers must not use it across multiple columns; see setFormula.
 */
export function expandPattern(pattern: string, row: number): string {
  return pattern.replace(/\{r([+-]\d+)?\}/g, (_match, offset: string | undefined) => {
    return String(row + 1 + (offset ? Number(offset) : 0));
  });
}

/**
 * Compresses one column's formulas.
 *
 * `formulas` is indexed by offset from `startRow`; an empty string means the cell has
 * no formula, and those are skipped rather than treated as a value -- a gap does not
 * break a run, because a column with a blank in the middle is still one piece of
 * logic.
 *
 * A pattern shared by fewer than `minRunLength` cells is not worth a run: it is
 * reported as exceptions instead, which is the honest representation of a column of
 * unrelated one-off formulas.
 */
export function compressColumn(
  column: number,
  startRow: number,
  formulas: string[],
  minRunLength = 3
): CompressedColumn {
  const normalised = formulas.map((formula, index) =>
    formula ? normaliseFormula(formula, startRow + index) : ""
  );

  // Count each shape so we can tell a real fill-down from a handful of odd cells.
  const counts = new Map<string, number>();
  for (const shape of normalised) {
    if (shape) counts.set(shape, (counts.get(shape) ?? 0) + 1);
  }

  const patterns: FormulaPattern[] = [];
  const exceptions: FormulaException[] = [];

  let index = 0;
  while (index < normalised.length) {
    const shape = normalised[index];
    if (!shape) {
      index += 1;
      continue;
    }

    // Extend over contiguous cells with the same shape, stepping over blanks so a gap
    // does not split one fill-down into two runs.
    let end = index;
    let lastMatch = index;
    while (end + 1 < normalised.length) {
      const next = normalised[end + 1];
      if (next === shape) {
        lastMatch = end + 1;
        end += 1;
      } else if (next === "") {
        end += 1;
      } else {
        break;
      }
    }
    end = lastMatch;

    const runLength = end - index + 1;
    if (runLength >= minRunLength || (counts.get(shape) ?? 0) >= minRunLength) {
      patterns.push({
        range:
          runLength === 1
            ? cellAddress(startRow + index, column)
            : `${cellAddress(startRow + index, column)}:${cellAddress(startRow + end, column)}`,
        pattern: shape,
        cellCount: runLength,
      });
    } else {
      for (let offset = index; offset <= end; offset += 1) {
        if (normalised[offset]) {
          exceptions.push({
            address: cellAddress(startRow + offset, column),
            formula: formulas[offset],
          });
        }
      }
    }

    index = end + 1;
  }

  return { patterns, exceptions };
}

/**
 * Compresses a whole rectangle of formulas, column by column.
 *
 * `grid` is row-major, as the Excel API returns it. The result is ordered by column so
 * a reader scanning it sees the sheet's logic laid out left to right.
 */
export function compressFormulaGrid(
  grid: string[][],
  startRow = 0,
  startColumn = 0,
  minRunLength = 3
): CompressedColumn {
  const rowCount = grid.length;
  const columnCount = rowCount > 0 ? Math.max(...grid.map((row) => row.length)) : 0;

  const patterns: FormulaPattern[] = [];
  const exceptions: FormulaException[] = [];

  for (let column = 0; column < columnCount; column += 1) {
    const columnFormulas: string[] = [];
    for (let row = 0; row < rowCount; row += 1) {
      columnFormulas.push(grid[row][column] ?? "");
    }
    if (!columnFormulas.some(Boolean)) continue;

    const compressed = compressColumn(startColumn + column, startRow, columnFormulas, minRunLength);
    patterns.push(...compressed.patterns);
    exceptions.push(...compressed.exceptions);
  }

  return { patterns, exceptions };
}

/** For the elision sentence: "C, D and F" rather than a list of indices. */
export function describeColumns(indices: number[]): string {
  const letters = indices.map(columnLetter);
  if (letters.length <= 1) return letters.join("");
  return `${letters.slice(0, -1).join(", ")} and ${letters[letters.length - 1]}`;
}
